import { supabase } from '@/integrations/supabase/client';
import { ApiService } from '@/services/api/ApiService';
import {
  ActivationPackWithTarget,
  CachedAssetEntry,
  GeneratedAsset,
  GeneratedAssetCache,
  GeneratedAssetType,
} from '@/types/activation/activation-pack';
import { AssetGeneratorService } from '@/services/activation/AssetGeneratorService';

const BUCKET_NAME = 'activation-pack-assets';

const EXTENSION_BY_TYPE: Record<GeneratedAssetType, string> = {
  qr_png: 'png',
  qr_svg: 'svg',
  product_qr_png: 'png',
  product_qr_svg: 'svg',
  sticker_pdf: 'pdf',
  email_signature_html: 'html',
  social_card_png: 'png',
  embed_code: 'html',
  lookbook_pdf: 'pdf',
  app_poster_png: 'png',
  app_poster_pdf: 'pdf',
  poster_pack_pdf: 'pdf',
};

/** Text-only assets are inlined on the pack row; no upload needed. */
const TEXT_ASSET_TYPES: ReadonlySet<GeneratedAssetType> = new Set([
  'email_signature_html',
  'embed_code',
]);

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const generationStoragePath = (
  packId: string,
  generationId: string,
  type: GeneratedAssetType,
) => `pack-${packId}/generations/${generationId}/${type}.${EXTENSION_BY_TYPE[type]}`;

export interface UploadedActivationDesignPdf {
  url: string;
  storagePath: string;
}

async function dataToBlob(asset: GeneratedAsset): Promise<Blob> {
  if (asset.data instanceof Blob) return asset.data;
  if (typeof asset.data === 'string') {
    if (asset.data.startsWith('data:')) {
      const res = await fetch(asset.data);
      return await res.blob();
    }
    if (/^https?:\/\//i.test(asset.data)) {
      const res = await fetch(asset.data);
      if (!res.ok) {
        throw new Error(`Failed to read ${asset.type} (${res.status})`);
      }
      return await res.blob();
    }
    return new Blob([asset.data], { type: asset.mimeType });
  }
  throw new Error(`Unsupported asset data type for ${asset.type}`);
}

export class ActivationPackAssetStorageService {
  /**
   * Upload one binary asset to an immutable per-build path and return its
   * public URL. A rejected/stale build therefore cannot overwrite live files.
   */
  static async uploadAsset(
    packId: string,
    asset: GeneratedAsset,
    generationId: string,
  ): Promise<CachedAssetEntry> {
    if (TEXT_ASSET_TYPES.has(asset.type)) {
      // Inline text assets — no storage upload
      return {
        data: typeof asset.data === 'string' ? asset.data : '',
        filename: asset.filename,
        mimeType: asset.mimeType,
        generated_at: new Date().toISOString(),
      };
    }

    const blob = await dataToBlob(asset);
    const storagePath = generationStoragePath(packId, generationId, asset.type);

    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(storagePath, blob, {
        contentType: asset.mimeType,
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Failed to upload ${asset.type}: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(storagePath);

    return {
      url: publicUrl,
      filename: asset.filename,
      mimeType: asset.mimeType,
      generated_at: new Date().toISOString(),
    };
  }

  /**
   * Generate every applicable asset for a pack, upload binary ones to storage,
   * and persist the resulting URL/data cache to the pack row. Subsequent visits
   * to the detail page hydrate directly from this cache — no client-side
   * canvas/jsPDF work at all.
   */
  static async buildAndPersist(
    pack: ActivationPackWithTarget,
    prebuiltAssets?: GeneratedAsset[],
  ): Promise<GeneratedAssetCache> {
    const expectedAssetInputRevision = pack.asset_input_revision || '';
    const expectedAssetCacheRevision = pack.asset_cache_revision || '';
    if (
      !UUID_PATTERN.test(expectedAssetInputRevision) ||
      !UUID_PATTERN.test(expectedAssetCacheRevision)
    ) {
      throw new Error('Reload this activation pack before building its artwork');
    }

    const assets = prebuiltAssets ?? await AssetGeneratorService.generateAllAssets(pack);
    if (assets.length === 0) {
      throw new Error('No activation artwork was generated');
    }

    // Every browser build gets immutable object paths. If this build loses the
    // revision CAS, its objects are harmless orphans rather than overwrites of
    // the accepted files referenced by a printer draft.
    const nextAssetCacheRevision = crypto.randomUUID();
    const generationId = nextAssetCacheRevision;
    const cache: GeneratedAssetCache = { ...(pack.generated_assets || {}) };
    for (const asset of assets) {
      cache[asset.type] = await this.uploadAsset(
        pack.id,
        asset,
        generationId,
      );
    }

    // Publish only if both the generator inputs and the previously accepted
    // cache are still exactly the versions this build started from. Never
    // delete this immutable generation when the API outcome is ambiguous: the
    // CAS may have committed before its response was lost, in which case the
    // accepted cache already references these paths. Unreferenced generations
    // are safe orphans and can be reaped later by a server-side job.
    try {
      await ApiService.invoke({
        resource: 'activation-packs',
        action: 'update',
        id: pack.id,
        data: {
          generated_assets: cache,
          expectedAssetInputRevision,
          expectedAssetCacheRevision,
          nextAssetCacheRevision,
        },
      });
    } catch (publishError) {
      // The update can commit even if its HTTP response is lost. Verify the
      // client-chosen next revision before reporting failure or encouraging a
      // retry; matching means this exact immutable generation was accepted.
      try {
        const recovered = await ApiService.invoke<{
          activationPack?: ActivationPackWithTarget;
        }>({
          resource: 'activation-packs',
          action: 'get',
          id: pack.id,
        });
        if (
          recovered?.activationPack?.asset_cache_revision ===
          nextAssetCacheRevision
        ) {
          return cache;
        }
      } catch {
        // Preserve the original publication error when recovery cannot be
        // confirmed. The caller still refetches the pack before another try.
      }
      throw publishError;
    }
    return cache;
  }

  /**
   * Upload a selected design to an immutable attempt path. The caller only
   * adds the returned URL to a draft through an artwork-revision CAS, so a
   * slow render can never overwrite or enter a newer pack's print order.
   */
  static async uploadDesignPdf(
    packId: string,
    kind: 'lookbook' | 'poster',
    designId: string,
    blob: Blob,
  ): Promise<UploadedActivationDesignPdf> {
    const generationId = crypto.randomUUID();
    const storagePath =
      `pack-${packId}/design-prints/${generationId}/${kind}-${designId}.pdf`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(storagePath, blob, {
        contentType: 'application/pdf',
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Failed to upload ${kind} design "${designId}": ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(storagePath);

    return { url: publicUrl, storagePath };
  }

  /**
   * Convert a cached entry back into a GeneratedAsset-shaped object for the
   * UI to render/download. For URL-backed assets we leave `data` as the URL
   * string (consumers can fetch on demand to get the blob).
   */
  static cacheToAsset(
    type: GeneratedAssetType,
    entry: CachedAssetEntry,
    label: string,
    description: string,
  ): GeneratedAsset {
    return {
      type,
      label,
      description,
      data: entry.data !== undefined ? entry.data : entry.url || '',
      filename: entry.filename,
      mimeType: entry.mimeType,
    };
  }
}
